import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import { BuildingMenuType } from '@/enums';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import type { ListTypeListItem } from '@/types/list-type';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCustomMenuContent } from '@/hooks/custom-menu';
import { useTranslation } from 'react-i18next';
import PopupEventDetail from '@/components/feature/event/popup-event-detail/popup-event-detail';
import styles from './styles.module.scss';
import VirtualizedEventCarousel from '@/components/feature/event/VirtualizedEventCarousel';

const EventPage = () => {
    const { t } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const { isHorizontal } = useOrientation();
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [content, setContent] = useState<ListTypeListItem | undefined>(undefined);

    const customContent = useCustomMenuContent(gnbMenuId);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        if (gnbMenuId) {
            return listMenus.find((menu) => menu.id === gnbMenuId && menu.menuType === BuildingMenuType.CUSTOM_MENU);
        }
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.CUSTOM_MENU);
    }, [server?.buildingMenus, ui.language, gnbMenuId]);

    const handleItemClick = (item: ListTypeListItem) => {
        if (item.qrConnect) {
            setIsOpenDetail(true);
            setContent(item);
        }
    };

    const pageClassname = useMemo(() => {
        return clsx(styles['event'], {
            [styles['event__vertical']]: !isHorizontal,
            [styles['event__horizontal']]: isHorizontal,
            [styles['event__contrast']]: ui.contrast,
            [styles['event__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['event__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['event__carousel'], {
            [styles['event__carousel__horizontal']]: isHorizontal,
            [styles['event__carousel__vertical']]: !isHorizontal,
            [styles['event__carousel__contrast']]: ui.contrast,
            [styles['event__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['event__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['event__carousel__vertical__empty']]: customContent.length === 0 && !isHorizontal,
            [styles['event__carousel__vertical__empty__halfmode']]: customContent.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['event__carousel__horizontal__empty']]: customContent.length === 0 && isHorizontal,
            [styles['event__carousel__horizontal__empty__halfmode']]: customContent.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, customContent.length]);

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['event__header'], {
                    [styles['event__header--horizontal']]: isHorizontal,
                    [styles['event__header--vertical']]: !isHorizontal,
                    [styles['event__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader
                    title={(currentMenu?.name as string) || ''}
                    description={currentMenu?.description || ''}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>

            <div className={carouselClassname}>
                {customContent.length > 0 ? (
                    <VirtualizedEventCarousel contents={customContent} onClickItem={handleItemClick} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="event-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>
            {isOpenDetail && <PopupEventDetail content={content} isOpen={isOpenDetail} onClose={() => setIsOpenDetail(false)} />}
        </div>
    );
};

export default EventPage;
